"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getJEPFromEBTSBuffer = exports.getImagesFromEBTSObject = exports.useGroup = exports.getImageRecordFromASCIIRecord = exports.getImageFromASCIIImageRecord = exports.getBioTypeFromImageRecord = exports.getJEPFromEBTSObject = exports.isImageRecord = exports.isRecordBiographics = exports.isRecordEmpty = exports.parseEBTS = void 0;
/**
 * EBTS serialization module
 * This module contains functions to serialize and deserialize EBTS files
 */
const jfd_1 = require("@le2/jfd");
const uuid_1 = require("uuid");
const ebts_1 = require("./ebts");
const ebts_parser_1 = require("./ebts-parser");
const errors_1 = require("./errors");
const types_1 = require("./types");
const IMAGE_FIELDS_WHITELIST = ['ImageType', 'PhotoDate', 'groupDateTime', 'NCICDesignationCode', 'SMTDescriptors'];
/**
 * Convert an EBTS buffer into a regular Javascript object notation
 * @throws Error if parser error occurs while processing ebts file
 */
function parseEBTS(ebtsFile) {
    // Perform the parsing. Any errors will be thrown
    let ebtsString = ebtsFile.toString('binary');
    try {
        return ebts_parser_1.parser.parse(ebtsString);
    }
    catch (err) {
        // Create a custom error with context or re-throw
        if (err.location) {
            throw new errors_1.EBTSParseError(err.message, ebtsString, err.location);
        }
        throw err;
    }
}
exports.parseEBTS = parseEBTS;
/**
 * Check if a record is empty
 * @param record
 */
function isRecordEmpty(record) {
    var _a, _b;
    return ((_b = (_a = record.fields) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) === 0;
}
exports.isRecordEmpty = isRecordEmpty;
/**
 * Check if a record contains biographics data
 * @param record
 */
function isRecordBiographics(record) {
    // Use the string representation of the type to check for equality
    const recordType = record.recordType.toString();
    return recordType === types_1.EbtsRecordType.Type1 || recordType === types_1.EbtsRecordType.Type2;
}
exports.isRecordBiographics = isRecordBiographics;
/**
 * Check if a record contains image data
 * @param record
 */
function isImageRecord(record) {
    // Assume biographics shouldn't have images
    return !isRecordBiographics(record) && !!record.image;
}
exports.isImageRecord = isImageRecord;
/**
 * Generate a JEP from the passed ebtsObject parsed against the JFD instance
 * @param ebtsObject
 * @param jfd
 * @param whitelist - a list of field names that could be imported
 */
function getJEPFromEBTSObject(ebtsObject, jfd, whitelist) {
    // Reduce the EBTS object into a JEP
    return ebtsObject
        .filter((record) => {
        // Skip if the record has no fields or is not biographics
        return !isRecordEmpty(record) && isRecordBiographics(record);
    })
        .reduce((jep, record) => {
        const reducedRecord = reduceRecord(jfd, record, whitelist);
        return Object.assign(Object.assign({}, jep), reducedRecord);
    }, {});
}
exports.getJEPFromEBTSObject = getJEPFromEBTSObject;
/**
 * Get the BioType from information in the image record
 * @param imageJep
 */
function getBioTypeFromImageRecord(imageJep) {
    switch (imageJep['ImageType']) {
        case 'SCAR':
        case 'MARK':
        case 'TATTOO':
            return jfd_1.BioType.SMT;
        case 'FACE':
            const subjectPose = imageJep['SubjectPose'];
            // TODO: Dynamic 10.020 POSE field name information in the JFD?
            const faceBioType = types_1.subjectPoseBioTypeMap[subjectPose];
            if (!faceBioType) {
                throw new Error(`Can't determine the FACE BioType for the given pose: ${subjectPose}`);
            }
            return faceBioType;
        default:
            throw new Error(`Can't determine BioType from ImageType: ${imageJep['ImageType']}.`);
    }
}
exports.getBioTypeFromImageRecord = getBioTypeFromImageRecord;
/**
 * Get an image structure from an ASCII Image record e.g. Type-10
 * @param record
 * @param jfd
 */
function getImageFromASCIIImageRecord(record, jfd) {
    const imageJep = reduceRecord(jfd, record);
    const whitelistedJep = whitelistJEP(imageJep, IMAGE_FIELDS_WHITELIST);
    return {
        bioType: getBioTypeFromImageRecord(imageJep),
        imageId: uuid_1.v4(),
        groupId: '0',
        metadata: Object.assign({}, whitelistedJep)
    };
}
exports.getImageFromASCIIImageRecord = getImageFromASCIIImageRecord;
/**
 * Get an ImageRecord from an EBTS record
 * @param record
 * @param jfd
 * @param getNextGroup - State callback to maintain group logic _ala_ React
 */
function getImageRecordFromASCIIRecord(record, jfd, getNextGroup // Group logic state callback ala React
) {
    const image = getImageFromASCIIImageRecord(record, jfd);
    const imageId = image.imageId;
    const rawImage = {
        imageId,
        data: Buffer.from(record.image, 'base64')
    };
    const group = getNextGroup(image.bioType);
    // Set to the same group (also assume image has metadata)
    image.metadata.groupDateTime = group.toISOString();
    return {
        imageId,
        info: image,
        data: rawImage,
        idc: 0 // IDC does not matter here as it's going to be ignored by the metadata whitelist
    };
}
exports.getImageRecordFromASCIIRecord = getImageRecordFromASCIIRecord;
/**
 * Initialize the state for the grouping logic _ala_ React
 */
function useGroup() {
    let bioTypeMarker = null;
    let group = new Date(new Date().setSeconds(0, 0));
    let groupBioTypes = new Set();
    const advanceGroup = function () {
        return new Date(group.getTime() + 60000);
    };
    /**
     * Callback function to process the current BioType and return a new group, or fail accordingly
     * @param currentBioType
     */
    return function (currentBioType) {
        // Skip repetition and group checks if the BioType's modality is not Face, and always start a new group
        // TODO: Validate supported modalities? This just assumes that if it's not Face it may still be valid
        if (!jfd_1.ModalityBioTypeMap[jfd_1.Modality.Face].includes(currentBioType)) {
            // Start a new group with a date +1 minute
            group = advanceGroup();
            return group;
        }
        // Initialize BioType marker
        if (!bioTypeMarker) {
            bioTypeMarker = currentBioType;
        }
        // Check for the start of the next group and clear list of found BioTypes
        else if (currentBioType === bioTypeMarker) {
            groupBioTypes = new Set();
            group = advanceGroup();
        }
        // Check for repeated BioTypes
        else if (groupBioTypes.has(currentBioType)) {
            throw new errors_1.RepeatedBioTypeError(bioTypeMarker, currentBioType);
        }
        // Continue using the same group and keep track of the BioTypes found so far
        groupBioTypes.add(currentBioType);
        return group;
    };
}
exports.useGroup = useGroup;
/**
 * Generate a list of mapped Images from the passed ebtsObject parsed against the JFD instance
 * @param ebtsObject
 * @param jfd
 */
function getImagesFromEBTSObject(ebtsObject, jfd) {
    let lastRecordType = null;
    let getNextGroup;
    // Reduce the EBTS object into Image[]
    return ebtsObject.filter(isImageRecord).reduce((images, record) => {
        const recordType = `${record.recordType}`;
        // Extract image data according to record Type
        if (recordType === types_1.EbtsRecordType.Type4) {
            // TODO: Handle Type4
            console.warn(`TODO: Type-4 image record.`);
        }
        else if (recordType === types_1.EbtsRecordType.Type10) {
            try {
                // TODO: Here we assume record Types are ordered
                if (lastRecordType !== recordType) {
                    lastRecordType = recordType;
                    getNextGroup = useGroup();
                }
                const imageRecord = getImageRecordFromASCIIRecord(record, jfd, getNextGroup);
                return [...images, imageRecord];
            }
            catch (e) {
                // Halt on RepeatedBioTypeError errors
                if (e instanceof errors_1.RepeatedBioTypeError) {
                    throw e;
                }
                // Just print a warning and continue processing
                console.warn(`Could not get image data from record: ${e.message}`);
            }
        }
        return images;
    }, []);
}
exports.getImagesFromEBTSObject = getImagesFromEBTSObject;
/**
 * This function solely exists to reduce boilerplate code when using the serialization API
 * @param ebtsFile
 * @param jfd
 */
function getJEPFromEBTSBuffer(ebtsFile, jfd) {
    const ebtsObject = parseEBTS(ebtsFile);
    return getJEPFromEBTSObject(ebtsObject, jfd);
}
exports.getJEPFromEBTSBuffer = getJEPFromEBTSBuffer;
/**
 * Reduce an EBTS record to a JEP
 * @param jfd
 * @param r
 * @param whitelist
 */
function reduceRecord(jfd, r, whitelist) {
    return r.fields.reduce((jep, ebtsField) => {
        const kf = locationIndexToKeyAndField(jfd.jfdDef, ebtsField.index);
        if (!kf) {
            // Just skip if the field was not found
            return jep;
        }
        // We can be sure the field exists
        const [name, field] = kf;
        // Skip if the field is not whitelisted
        if (whitelist && !whitelist.includes(field.name)) {
            return jep;
        }
        const reducedField = reduceField(name, field, ebtsField);
        if (ebts_1.isValueEmpty(Object.values(reducedField)[0])) {
            // Skip empty field data
            return jep;
        }
        // Augment JEP
        return Object.assign(Object.assign({}, jep), reducedField);
    }, {});
}
/**
 * @param jep
 * @param whitelist - A list of mnemonics of only the fields we want to keep from this record
 */
function whitelistJEP(jep, whitelist) {
    return Object.entries(jep).reduce((newJep, [key, value]) => {
        if (whitelist.includes(key)) {
            return Object.assign(Object.assign({}, newJep), { [key]: value });
        }
        return newJep;
    }, {});
}
/**
 * Guard a field to ensure the date value is converted correctly, if applicable
 */
function ebtsDateFieldFormatGuard(field, value) {
    var _a;
    // There is no enum but assume this is enough to validate a field's type
    // Also attempt to guard only if there's a non-empty value
    if (value && field.type === 'date') {
        // Default to YYYYMMDD, but allow EBST.format to override this
        return ebts_1.getISODateStringFromEBTSDateString(value, ((_a = field.EBTS) === null || _a === void 0 ? void 0 : _a.format) || 'YYYYMMDD');
    }
    return value;
}
/**
 * Reduce an EBTS object field into a JEP
 * @param name
 * @param field
 * @param ebtsField
 */
function reduceField(name, field, ebtsField) {
    // Check if there's a mnemonic, name and that it's not blacklisted
    const fieldType = field.getType();
    switch (fieldType) {
        case 'SONS':
            return { [name]: ebtsDateFieldFormatGuard(field, ebtsField.instances[0][0]) };
        case 'MONS':
            return { [name]: ebtsField.instances.flat().map((value) => ebtsDateFieldFormatGuard(field, value)) };
        case 'SOS': {
            const [ebtsInstance] = ebtsField.instances;
            return { [name]: reduceSOS(field, ebtsInstance) };
        }
        case 'MOS': {
            const ebtsInstances = ebtsField.instances;
            const reducedMos = ebtsInstances.reduce((p, ebtsInstance) => {
                const subs = reduceSOS(field, ebtsInstance);
                return [...p, subs];
            }, []);
            return { [name]: reducedMos };
        }
        default:
            throw new Error(`Unknown field type: ${fieldType}`);
    }
}
/**
 * Reduce an ebts field into key:value records
 * @param field
 * @param ebtsInstance
 */
function reduceSOS(field, ebtsInstance) {
    const names = getSOSSubfieldNames(field);
    return names.reduce((subfields, name, idx) => {
        const value = ebtsInstance[idx] || '';
        return Object.assign(Object.assign({}, subfields), { [name]: ebtsDateFieldFormatGuard(field, value) }); // Coerce into an empty string to avoid validation issues
    }, {});
}
/**
 * Return the key and field for a given Location Index
 * @param jfdDef
 * @param locationIndex
 */
function locationIndexToKeyAndField(jfdDef, locationIndex) {
    return Object.entries(jfdDef.fields)
        .filter(([_, field]) => !!field.EBTS)
        .find(([_, field]) => 
    // Now we are sure that the EBTS field is present
    indexesAreEquivalent(field.EBTS.index, locationIndex));
}
/**
 * This function allows us to compare 1.01 and 1.001, for example, and declare them equal
 * @param index1
 * @param index2
 */
function indexesAreEquivalent(index1, index2) {
    return ebts_1.sanitizeIndex(index1) === ebts_1.sanitizeIndex(index2);
}
/**
 * Return array of subfield names for a given header field name
 * @param field
 */
function getSOSSubfieldNames(field) {
    // Assume the field is SOS/MOS
    return Object.values(field.subFields.fields).map(({ name }) => name);
}
//# sourceMappingURL=serialization.js.map